import { DomainError } from "@econ-v1/domain";
import type { HomeNodeClient, OutboxRecord, RepositoryJsonValue } from "@econ-v1/ports";
import type { OutboxDelivery } from "@econ-v1/kernel";

export interface ClientSyncOutboxRoute {
  readonly capability: string;
  readonly domain: string;
  readonly encode: (payload: RepositoryJsonValue) => RepositoryJsonValue;
}

export const DEFAULT_CLIENT_SYNC_OUTBOX_ROUTES: readonly ClientSyncOutboxRoute[] = [{
  capability: "core.conversation.send_message",
  domain: "conversation",
  encode: encodeConversationCommand,
}];

/**
 * Delivers durable local intents through the server's idempotent client-sync
 * command endpoint. Routes are explicit: a local domain operation is not
 * implicitly treated as a server capability.
 */
export function createClientSyncOutboxDelivery(
  homeNode: HomeNodeClient,
  routes: readonly ClientSyncOutboxRoute[],
): OutboxDelivery {
  const routeByDomain = new Map(routes.map((route) => [route.domain, route]));
  if (routeByDomain.size !== routes.length) {
    throw new TypeError("Client-sync outbox routes must use unique domains");
  }
  return async (intent: OutboxRecord): Promise<void> => {
    const route = routeByDomain.get(intent.domain);
    if (route === undefined) {
      throw new DomainError({
        code: "capability_not_declared",
        message: `No client-sync outbox route is registered for ${intent.domain}`,
      });
    }
    await homeNode.invoke({
      headers: { "Idempotency-Key": intent.idempotencyKey },
      operation: `POST /api/v2/client-sync/${encodeURIComponent(intent.domain)}/commands`,
      payload: {
        capability: route.capability,
        payload: route.encode(intent.payload),
      },
    });
  };
}

function encodeConversationCommand(payload: RepositoryJsonValue): RepositoryJsonValue {
  if (payload === null || Array.isArray(payload) || typeof payload !== "object") {
    throw new DomainError({ code: "invalid_request", message: "Conversation outbox payload must be an object" });
  }
  if (payload.operation !== "conversation.send-message") {
    throw new DomainError({ code: "invalid_request", message: "Unsupported conversation outbox operation" });
  }
  const conversationId = text(payload.conversationId, "conversation id");
  const content = text(payload.body, "message body");
  const senderNodeId = text(payload.senderId, "sender node id");
  return {
    content,
    conversation_id: conversationId,
    sender_node_id: senderNodeId,
  };
}

function text(value: RepositoryJsonValue | undefined, label: string): string {
  if (typeof value !== "string" || value.trim() === "") {
    throw new DomainError({ code: "invalid_request", message: `${label} is required for outbox delivery` });
  }
  return value;
}
